// modules/reporteCliente/pages/ReporteClientePage.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, MenuItem, Button, Alert,
  CircularProgress, IconButton, InputAdornment, Paper,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel, Chip, TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon      from "@mui/icons-material/Search";
import CloseIcon       from "@mui/icons-material/Close";
import VisibilityIcon  from "@mui/icons-material/Visibility";
import DashboardLayout from "../../../layouts/DashboardLayout";
import axios from "../../../config/axiosConfig";
import { buildEquiposMap, extractList } from "../../requerimiento/utils/extract";
import PageHeader from "../../../components/layout/PageHeader";
import BusquedasRecientes, { useBusquedasRecientes } from "../../requerimiento/components/BusquedasRecientes";

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
  // El radio de esquina global (theme.shape.borderRadius=14) queda desproporcionado
  // en un input de esta altura y se ve como píldora — string con unidad explícita
  // para no chocar con el multiplicador ×14 que aplica sx a un borderRadius numérico.
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

// La columna "Acción" queda fija a la derecha: el botón Visualizar siempre está a la vista aunque la tabla tenga scroll horizontal.
const accionFijaSx = {
  position: "sticky",
  right: 0,
  zIndex: 2,
  width: 108,
  boxShadow: "-4px 0 6px -4px rgba(0,0,0,0.18)",
};

const EQUIPOS_LABELS = {
  modem: "Modem",
  chip: "Chip",
  radio: "Radio",
  bateria: "Bat.",
  varios1: "Var.1",
  varios2: "Var.2",
  placa: "Placa",
};

const STORAGE_KEY = "reporteCliente:listado";

const SERVICIO_COLORS = {
  Accesorios    : { bg: "#fef3c7", fg: "#92400e", border: "#f59e0b" },
  AVL           : { bg: "#dbeafe", fg: "#1d4ed8", border: "#60a5fa" },
  Energia       : { bg: "#fef9c3", fg: "#854d0e", border: "#eab308" },
  POC           : { bg: "#ede9fe", fg: "#6d28d9", border: "#a78bfa" },
  Scada         : { bg: "#dcfce7", fg: "#166534", border: "#22c55e" },
  Seguridad     : { bg: "#fee2e2", fg: "#b91c1c", border: "#f87171" },
  TKR           : { bg: "#ccfbf1", fg: "#0f766e", border: "#14b8a6" },
  "Ventas Varias": { bg: "#e5e7eb", fg: "#374151", border: "#9ca3af" },
};

const getServicioColor = (nombre) =>
  SERVICIO_COLORS[nombre] || { bg: "#f8fafc", fg: "#334155", border: "#cbd5e1" };

const loadSavedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const EquiposResumen = ({ equipos }) => {
  const entries = Object.entries(equipos || {}).filter(([, cantidad]) => Number(cantidad) > 0);
  if (!entries.length) return <Typography variant="caption" color="text.secondary">Sin equipos</Typography>;

  return (
    <Box sx={{ display: "flex", gap: 0.4, justifyContent: "center", flexWrap: "wrap", maxWidth: 190, mx: "auto" }}>
      {entries.map(([tipo, cantidad]) => (
        <Chip
          key={tipo}
          size="small"
          variant="outlined"
          label={`${EQUIPOS_LABELS[tipo] || tipo}: ${cantidad}`}
          sx={{ height: 18, fontSize: 10.5, "& .MuiChip-label": { px: 0.6 } }}
        />
      ))}
    </Box>
  );
};

const ReporteClientePage = () => {
  const navigate = useNavigate();
  const savedState = loadSavedState();
  const [buscar,      setBuscar]      = useState(savedState.buscar || "");
  const [codServicio, setCodServicio] = useState(savedState.codServicio || "Todos");
  const [estadoServicio, setEstadoServicio] = useState(savedState.estadoServicio || "Todos");
  const [servicios,   setServicios]   = useState([]);
  const [lista,       setLista]       = useState(Array.isArray(savedState.lista) ? savedState.lista : []);
  const [equiposMap,  setEquiposMap]  = useState(savedState.equiposMap || {});
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [buscado,     setBuscado]     = useState(Boolean(savedState.buscado));
  const [sortConfig,  setSortConfig]  = useState(savedState.sortConfig || { key: "id", direction: "desc" });
  const [page,        setPage]        = useState(Number(savedState.page || 1));
  const [pageSize,    setPageSize]    = useState(Number(savedState.pageSize || 30));
  const [total,       setTotal]       = useState(Number(savedState.total || 0));

  // Cargar catálogo de servicios
  useEffect(() => {
    axios.get("/api/clientes/catalogos")
      .then(({ data }) => {
        const raw = data?.data ?? data;
        setServicios(Array.isArray(raw?.servicios) ? raw.servicios : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const payload = {
      buscar,
      codServicio,
      estadoServicio,
      lista,
      equiposMap,
      buscado,
      sortConfig,
      page,
      pageSize,
      total,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [buscar, codServicio, estadoServicio, lista, equiposMap, buscado, sortConfig, page, pageSize, total]);

  // `f` permite repetir una búsqueda guardada sin esperar a que los filtros del formulario se actualicen.
  const handleBuscar = async (targetPage = 1, targetPageSize = pageSize, f = null) => {
    const q = f ? f.buscar : buscar;
    const cod = f ? f.codServicio : codServicio;
    const est = f ? f.estadoServicio : estadoServicio;
    setLoading(true);
    setError(null);
    try {
      const [res, respEquipos] = await Promise.all([
        axios.get("/api/facturas/reporte/busqueda", {
          params: {
            buscar     : q.trim() || undefined,
            cod_servicio: cod !== "Todos" ? cod : undefined,
            estado: est !== "Todos" ? est : undefined,
            solo_nombre: 1, // busca por cliente, grupo y nombre (no por descripción)
            page: targetPage,
            limit: targetPageSize,
          },
        }),
        axios.get("/api/cliente-servicio/equipos"),
      ]);
      const raw = res.data?.data ?? res.data;
      const items = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];
      setLista(items);
      setTotal(Array.isArray(raw) ? raw.length : Number(raw?.total || 0));
      setPage(Array.isArray(raw) ? 1 : Number(raw?.page || targetPage));
      setPageSize(Array.isArray(raw) ? items.length || targetPageSize : Number(raw?.limit || targetPageSize));
      setEquiposMap(buildEquiposMap(extractList(respEquipos?.data)));
      setBuscado(true);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al buscar");
    } finally {
      setLoading(false);
    }
  };

  // Búsquedas recientes: se guardan con los nombres comunes del hook (nombreCliente/estado/codServicio).
  const ejecutar = (filtros) => {
    const f = { buscar: filtros.nombreCliente || "", codServicio: filtros.codServicio || "Todos", estadoServicio: filtros.estado || "Todos" };
    setBuscar(f.buscar); setCodServicio(f.codServicio); setEstadoServicio(f.estadoServicio);
    handleBuscar(1, pageSize, f);
  };
  const { recientes, buscar: buscarYRecordar, repetir, quitar, limpiar } = useBusquedasRecientes("reporte_cliente_busquedas_recientes", ejecutar);
  const lanzarBusqueda = () => buscarYRecordar({ nombreCliente: buscar, estado: estadoServicio, codServicio: codServicio === "Todos" ? "" : codServicio });

  // La X del buscador borra el texto y también la lista de resultados (vuelve al estado inicial, sin búsqueda).
  const limpiarBusqueda = () => {
    setBuscar("");
    setLista([]);
    setEquiposMap({});
    setTotal(0);
    setPage(1);
    setBuscado(false);
    setError(null);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") lanzarBusqueda(); };

  const getEquiposTotal = useCallback(
    (id) => Object.values(equiposMap[String(id)] || {})
      .reduce((acc, value) => acc + Number(value || 0), 0),
    [equiposMap]
  );

  const sortedLista = useMemo(() => {
    const rows = [...lista];
    const { key, direction } = sortConfig;
    const factor = direction === "asc" ? 1 : -1;

    const getValue = (row) => {
      if (key === "equipos_total") return getEquiposTotal(row.id);
      if (key === "tiene_factura") return row.tiene_factura ? 1 : 0;
      return row[key];
    };

    rows.sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * factor;
      return String(av ?? "").localeCompare(String(bv ?? ""), "es", { numeric: true, sensitivity: "base" }) * factor;
    });

    return rows;
  }, [lista, sortConfig, getEquiposTotal]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortableTh = ({ children, keyName, align, w }) => (
    <Th align={align} sx={w ? { width: w } : undefined}>
      <TableSortLabel
        hideSortIcon
        active={sortConfig.key === keyName}
        direction={sortConfig.key === keyName ? sortConfig.direction : "asc"}
        onClick={() => handleSort(keyName)}
      >
        {children}
      </TableSortLabel>
    </Th>
  );

  return (
    <DashboardLayout>
      <PageHeader
        title="Reporte Cliente"
        subtitle="Busqueda de servicios, facturas y equipos"
        showBack={false}
      />
      <Box sx={{ p: 1 }}>
        {/* ── Filtros ── */}
        <Paper variant="outlined" sx={{ p: 1, mb: 1 }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small" label="Razon social, grupo o nombre" sx={{ ...compactFieldSx, width: { xs: "100%", sm: 280 } }}
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
              onKeyDown={handleKeyDown}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {buscar && (
                      <IconButton size="small" aria-label="Borrar búsqueda" onClick={limpiarBusqueda}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={lanzarBusqueda} disabled={loading}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select size="small" label="Servicio" sx={{ ...compactFieldSx, width: 150 }}
              value={codServicio}
              onChange={(e) => setCodServicio(e.target.value)}
            >
              <MenuItem value="Todos">Todos</MenuItem>
              {servicios.map((s) => (
                <MenuItem key={s.id_servicio} value={s.id_servicio}>
                  {s.nombre}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select size="small" label="Estado" sx={{ ...compactFieldSx, width: 125 }}
              value={estadoServicio}
              onChange={(e) => setEstadoServicio(e.target.value)}
            >
              <MenuItem value="Todos">Todos</MenuItem>
              <MenuItem value="Activo">Activo</MenuItem>
              <MenuItem value="Inactivo">Inactivo</MenuItem>
            </TextField>

            <Button size="small" variant="contained" onClick={lanzarBusqueda} disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}>
              Buscar
            </Button>
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}

        {!buscado && !loading && (
          <BusquedasRecientes
            titulo="Busca un cliente para ver su reporte" ayuda="Escribe la razón social, el grupo o el nombre del servicio y presiona Enter. Sin texto lista todo; puedes acotar por servicio y estado."
            estadoOmitido="Todos" recientes={recientes} servicios={servicios} onRepetir={repetir} onQuitar={quitar} onLimpiar={limpiar}
          />
        )}

        {/* ── Resultados ── */}
        {buscado && (
          lista.length === 0 ? (
            <Alert severity="info" sx={{ py: 0.5 }}>No se encontraron registros.</Alert>
          ) : (
            <Paper variant="outlined">
              <TableContainer>
                <Table size="small" sx={{ tableLayout: "fixed", minWidth: 1110, "& td": { py: 0.25, px: 0.75, fontSize: "0.78rem" } }}>
                  <TableHead>
                    <TableRow>
                      <SortableTh keyName="cod_cliente" w={74}>Cod Cliente</SortableTh>
                      <SortableTh keyName="id" w={64}>Cod Servicio</SortableTh>
                      <SortableTh keyName="razon_social">Razón Social</SortableTh>
                      <SortableTh keyName="grupo" w={110}>Grupo</SortableTh>
                      <SortableTh keyName="nombre" w={110}>Nombre</SortableTh>
                      <SortableTh keyName="servicio_nombre" w={92}>Servicio</SortableTh>
                      <SortableTh keyName="tipo" w={70}>Tipo</SortableTh>
                      <SortableTh keyName="sucursal_facturar" w={88}>Sucursal</SortableTh>
                      <SortableTh keyName="estado" align="center" w={76}>Estado</SortableTh>
                      <SortableTh keyName="tiene_factura" align="center" w={64}>Factura</SortableTh>
                      <SortableTh keyName="equipos_total" align="center" w={140}>Equipos</SortableTh>
                      <Th align="center" sx={{ ...accionFijaSx, zIndex: 3 }}>Acción</Th>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedLista.map((r) => {
                      const servicioColor = getServicioColor(r.servicio_nombre);
                      return (
                      <TableRow
                        key={r.id}
                        hover
                        sx={{
                          bgcolor: (theme) => r.estado === "Inactivo" ? theme.palette.action.hover : "inherit",
                          "& .MuiTableCell-root": r.estado === "Inactivo" ? { color: "text.secondary" } : undefined,
                        }}
                      >
                        <TableCell>{r.cod_cliente}</TableCell>
                        <TableCell>{r.id}</TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.78rem", lineHeight: 1.25, overflowWrap: "anywhere" }}>
                            {r.razon_social}
                          </Typography>
                          {r.descripcion && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.72rem", lineHeight: 1.25, overflowWrap: "anywhere" }}>
                              {r.descripcion}
                            </Typography>
                          )}
                          {r.tiempo_conservacion_recorrido && (
                            <Typography
                              variant="caption"
                              title={`Guardar recorrido: ${r.tiempo_conservacion_recorrido}`}
                              sx={{ display: "block", mt: 0.15, fontSize: "0.68rem", lineHeight: 1.2, fontWeight: 700, color: "#d81b60" }}
                            >
                              {r.tiempo_conservacion_recorrido}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", overflowWrap: "anywhere" }}>{r.grupo || "-"}</TableCell>
                        <TableCell sx={{ verticalAlign: "top", overflowWrap: "anywhere" }}>{r.nombre || "-"}</TableCell>
                        <TableCell sx={{ verticalAlign: "top" }}>
                          <Chip
                            size="small"
                            label={r.servicio_nombre}
                            sx={{
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              bgcolor: servicioColor.bg,
                              color: servicioColor.fg,
                              border: `1px solid ${servicioColor.border}`,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        </TableCell>
                        <TableCell>{r.tipo}</TableCell>
                        <TableCell>{r.sucursal_facturar}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={r.estado || "-"}
                            size="small"
                            color={r.estado === "Activo" ? "success" : "default"}
                            sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={r.tiene_factura ? "Sí" : "No"}
                            size="small"
                            color={r.tiene_factura ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <EquiposResumen equipos={equiposMap[String(r.id)]} />
                        </TableCell>
                        <TableCell align="center" sx={{ ...accionFijaSx, bgcolor: "background.paper" }}>
                          <Button
                            size="small" variant="contained" disableElevation startIcon={<VisibilityIcon />}
                            // Botón sólido: el contorno del color principal casi no se distinguía sobre fondo oscuro
                            sx={{ minHeight: 26, fontSize: "0.72rem", fontWeight: 700, px: 1, textTransform: "none", whiteSpace: "nowrap" }}
                            onClick={() => navigate(`/reporte-cliente/${r.id}`)}
                          >
                            Visualizar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={Math.max(page - 1, 0)}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[15, 30, 50, 100, 200]}
                labelRowsPerPage="Limite"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                onPageChange={(_, newPage) => handleBuscar(newPage + 1, pageSize)}
                onRowsPerPageChange={(e) => handleBuscar(1, Number(e.target.value))}
                sx={{
                  minHeight: 36,
                  "& .MuiTablePagination-toolbar": { minHeight: 36, pl: 1 },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontSize: "0.78rem",
                  },
                }}
              />
            </Paper>
          )
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteClientePage;
